import React from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { getLocationReturnPath, navigateBackOrFallback, useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import {
  createSupplyRequisition,
  createSupplyRequisitionTemplate,
  importSupplyRequisitionFromExcel,
  peekSupplyReference,
  updateSupplyRequisition,
} from '../../../services/mutations';
import { supplyRequisitionRefPeekSnapshot } from '../peekRefSnapshot';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  DocumentTextIcon,
  FolderPlusIcon,
  Label,
  NumberInput,
  PlusIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
  UploadCloudIcon,
  MultiSelect,
} from '../../../components/ui';

import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { isSupplyRequisitionLockedAfterPipelineStep, moduleConfig } from './requisitionsModuleConfig';
import {
  SupplyRequisitionLine,
  SupplyRequisitionTemplate,
  useSupplyRequisition,
} from './useData';
import { formatCaseParenthetical } from './caseDisplay';

type LineForm = {
  key: string;
  /** Filters Item dropdown to catalog rows with this `StoreItem.level1` (empty = all). */
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  qty: string;
  rob: string;
  itemNotes: string;
};

type StoreItemCategoryResponse = {
  level1: string[];
  level2All: string[];
  level3All: string[];
  level2ByLevel1: Record<string, string[]>;
  level3ByLevel1Level2: Record<string, string[]>;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  level1Filter: '',
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  unitId: '',
  qty: '',
  rob: '',
  itemNotes: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

/** Qty to carry into a new requisition for delivery shortfalls / not marked received (see supply delivery line import). */
function followUpQtyFromDeliveryLine(line: {
  purchaseOrderQty?: number | null;
  deliveryQty?: number | null;
  isDelivered?: boolean;
}): number | null {
  const po = line.purchaseOrderQty;
  const d = line.deliveryQty;
  if (line.isDelivered === false) {
    if (po != null && d != null) {
      const rem = po - d;
      if (rem > 0) return rem;
      if (rem === 0) return po;
    }
    if (po != null) return po;
    if (d != null) return d;
    return null;
  }
  if (line.isDelivered === true && po != null && d != null && po !== d) {
    return Math.abs(po - d);
  }
  return null;
}

/** `YYYY-MM-DD` in local timezone (for date inputs). */
const getTodayLocalDateString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Single-line label: `vendorCode - name - (ucode)`; ucode from linked unit or `units` lookup. */
function formatRequisitionItemLabel(
  vendorCode: string | null | undefined,
  name: string | null | undefined,
  unitId: string | number | null | undefined,
  units: { id: number; ucode?: string }[],
  unitUcodeFromItem?: string | null,
): string {
  const v = (vendorCode ?? '').trim() || '';
  const n = (name ?? '').trim() || '';
  let ucode = (unitUcodeFromItem ?? '').trim();
  if (!ucode && unitId != null && unitId !== '') {
    const uid = typeof unitId === 'string' ? Number(unitId) : unitId;
    if (typeof uid === 'number' && !Number.isNaN(uid)) {
      ucode = units.find((x) => x.id === uid)?.ucode?.trim() ?? '';
    }
  }
  return ucode ? `${v} - ${n} - (${ucode})` : `${v} - ${n}`;
}

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const returnPath = React.useMemo(() => getLocationReturnPath(location.state), [location.state]);
  const isEdit = Boolean(id);
  const requisitionId = id ? Number(id) : undefined;
  const caseIdFromQuery = searchParams.get('caseId');
  const vesselIdFromQuery = searchParams.get('vesselId');
  const importFromQuery = searchParams.get('importFrom');
  const importFromDeliveryQuery = searchParams.get('importFromDelivery');
  const noCaseFromQuery = searchParams.get('noCase') === '1' || searchParams.get('noCase') === 'true';
  const prefillCaseId =
    !isEdit && !noCaseFromQuery && caseIdFromQuery && /^\d+$/.test(caseIdFromQuery) ? caseIdFromQuery : null;
  const lockCaseContext = Boolean(prefillCaseId);
  const lockNoCase = !isEdit && noCaseFromQuery;
  const { data: supplyCaseForPrefill } = useSWR<{
    id: number;
    ref?: string | null;
    vesselId?: number | null;
    vessel?: { id: number; vesselName?: string | null } | null;
  } | null>(prefillCaseId && !noCaseFromQuery ? `/supply-cases/${prefillCaseId}` : null, fetcher);

  const { data: refTemplates } = useSWR<
    Array<{ entityType: string; allowManualReferenceEdit?: boolean; isActive?: boolean; pattern?: string }>
  >('/reference-templates?moduleId=supplies', fetcher, { revalidateOnFocus: false });
  const allowManualRequisitionRef =
    refTemplates?.find((r) => r.entityType === 'SUPPLY_REQUISITION')?.allowManualReferenceEdit !== false;
  const hasActiveRequisitionTemplate = Boolean(
    refTemplates?.some(
      (r) =>
        r.entityType === 'SUPPLY_REQUISITION' &&
        r.isActive &&
        typeof r.pattern === 'string' &&
        r.pattern.trim().length > 0,
    ),
  );

  const { data: existing, isLoading: loadingExisting } = useSupplyRequisition(requisitionId);

  React.useEffect(() => {
    if (!isEdit || !existing?.id) return;
    if (isSupplyRequisitionLockedAfterPipelineStep(existing)) {
      navigate(moduleConfig.module.route.detail(existing.id), { replace: true });
    }
  }, [isEdit, existing, navigate]);
  const { data: casesRes } = useSWR<any>('/supply-cases?page=1&limit=300', fetcher);
  const cases = React.useMemo(
    () => (Array.isArray(casesRes) ? casesRes : casesRes?.data ?? []),
    [casesRes],
  );
  const { data: vessels = [] } = useSWR<any[]>('/vessels', fetcher);
  const { data: units = [] } = useSWR<any[]>('/store-units', fetcher);
  const { data: lineTemplates = [] } = useSWR<SupplyRequisitionTemplate[]>(
    '/supply-requisition-templates',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: storeCategories = [] } = useSWR<Array<{ id: number; name: string; abrv?: string | null }>>(
    '/store-categories',
    fetcher,
    { revalidateOnFocus: false },
  );
  const categoryMultiOptions = React.useMemo(
    () =>
      storeCategories.map((c) => ({
        value: c.id,
        label: c.abrv ? `${c.name} (${c.abrv})` : c.name,
      })),
    [storeCategories],
  );
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [templateApplyId, setTemplateApplyId] = React.useState('');
  const [saveTemplateOpen, setSaveTemplateOpen] = React.useState(false);
  const [deleteAllLinesOpen, setDeleteAllLinesOpen] = React.useState(false);
  const [importExcelBusy, setImportExcelBusy] = React.useState(false);
  const excelImportInputRef = React.useRef<HTMLInputElement>(null);
  const reqRefPeekTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  type HeaderForm = {
    reqDate: string;
    caseId: string;
    vesselId: string;
    categoryIds: number[];
    ref: string;
    refN: string;
    notes: string;
  };

  const [formData, setFormData] = React.useState<HeaderForm>(() => ({
    reqDate: id ? '' : getTodayLocalDateString(),
    caseId: '',
    vesselId: '',
    categoryIds: [],
    ref: '',
    refN: '',
    notes: '',
  }));

  React.useEffect(() => {
    if (!existing) return;
    const catIds =
      Array.isArray(existing.categoryIds) && existing.categoryIds.length > 0
        ? [...existing.categoryIds]
        : (existing.categories?.map((c) => c.id).filter((id): id is number => typeof id === 'number') ?? []);
    setFormData({
      reqDate: existing.reqDate ? existing.reqDate.slice(0, 10) : '',
      caseId: existing.caseId ? String(existing.caseId) : '',
      vesselId: existing.vesselId ? String(existing.vesselId) : '',
      categoryIds: catIds,
      ref: existing.ref || '',
      refN: existing.refN !== null && existing.refN !== undefined ? String(existing.refN) : '',
      notes: existing.notes || '',
    });

    const mapped = (existing.lines || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty !== null && line.qty !== undefined ? String(line.qty) : '',
      rob: line.rob !== null && line.rob !== undefined ? String(line.rob) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [existing]);

  React.useEffect(() => {
    if (isEdit || !prefillCaseId) return;
    setFormData((s) => (s.caseId === prefillCaseId ? s : { ...s, caseId: prefillCaseId }));
  }, [isEdit, prefillCaseId]);

  React.useEffect(() => {
    if (isEdit || !supplyCaseForPrefill || noCaseFromQuery) return;
    setFormData((s) => ({
      ...s,
      caseId: String(supplyCaseForPrefill.id),
      vesselId:
        supplyCaseForPrefill.vesselId != null && supplyCaseForPrefill.vesselId !== undefined
          ? String(supplyCaseForPrefill.vesselId)
          : '',
    }));
  }, [isEdit, supplyCaseForPrefill, noCaseFromQuery]);

  React.useEffect(() => {
    if (isEdit || !vesselIdFromQuery || !/^\d+$/.test(vesselIdFromQuery)) return;
    if (lockCaseContext) return;
    setFormData((s) => (s.vesselId ? s : { ...s, vesselId: vesselIdFromQuery }));
  }, [isEdit, vesselIdFromQuery, lockCaseContext]);

  React.useEffect(() => {
    if (isEdit || !lockNoCase) return;
    setFormData((s) => (s.caseId === '' ? s : { ...s, caseId: '' }));
  }, [isEdit, lockNoCase]);

  React.useEffect(() => {
    if (!isEdit) {
      supplyRequisitionRefPeekSnapshot.reset();
    }
  }, [isEdit]);

  /** Preview next reference (peek does not consume seq). Omit unchanged value on create so the server allocates. */
  React.useEffect(() => {
    if (isEdit) return;

    const reqDate = formData.reqDate;
    const vesselRaw = formData.vesselId;
    const categoryIds = formData.categoryIds ?? [];
    const caseRaw = formData.caseId?.trim();

    if (reqRefPeekTimerRef.current) clearTimeout(reqRefPeekTimerRef.current);
    reqRefPeekTimerRef.current = setTimeout(() => {
      reqRefPeekTimerRef.current = undefined;
      void (async () => {
        if (!reqDate) return;
        const vesselId =
          vesselRaw === '' || vesselRaw === undefined || vesselRaw === null ? null : Number(vesselRaw);
        if (vesselId != null && !Number.isFinite(vesselId)) return;
        const cats = categoryIds.filter((id) => typeof id === 'number' && Number.isFinite(id));
        const caseId = caseRaw && /^\d+$/.test(caseRaw) ? Number(caseRaw) : undefined;

        try {
          const { ref } = await peekSupplyReference({
            entityType: 'SUPPLY_REQUISITION',
            vesselId,
            categoryIds: cats,
            dateForYear: reqDate,
            caseId: caseId ?? null,
          });
          if (ref != null && ref.trim().length > 0) {
            const trimmed = ref.trim();
            setFormData((s) => {
              if (!supplyRequisitionRefPeekSnapshot.shouldApplyPeekToForm(s.ref)) return s;
              supplyRequisitionRefPeekSnapshot.setFromPeek(trimmed);
              return s.ref === trimmed ? s : { ...s, ref: trimmed };
            });
          }
        } catch {
          /* optional preview */
        }
      })();
    }, 200);

    return () => {
      if (reqRefPeekTimerRef.current) clearTimeout(reqRefPeekTimerRef.current);
    };
  }, [isEdit, formData.reqDate, formData.vesselId, formData.caseId, formData.categoryIds]);

  const importFromLinesLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit) return;
    if (!importFromQuery || !/^\d+$/.test(importFromQuery)) return;
    if (importFromLinesLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const full = (await fetcher(`/supply-requisitions/${importFromQuery}`)) as { lines?: SupplyRequisitionLine[] };
        if (cancelled) return;
        const mapped: LineForm[] = (full.lines || []).map((line) => ({
          key: `line-${line.id ?? Date.now()}-${Math.random()}`,
          level1Filter: (line.item?.level1 ?? '').trim(),
          itemId: line.itemId != null ? String(line.itemId) : '',
          itemVcode: line.itemVcode || '',
          itemDescr: line.itemDescr || '',
          unitId: line.unitId != null ? String(line.unitId) : '',
          qty: line.qty !== null && line.qty !== undefined ? String(line.qty) : '',
          rob: line.rob !== null && line.rob !== undefined ? String(line.rob) : '',
          itemNotes: line.itemNotes || '',
        }));
        if (mapped.length > 0) {
          setLines(mapped);
          importFromLinesLoadedRef.current = true;
        }
      } catch {
        // keep default line row
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, importFromQuery]);

  const importFromDeliveryLinesLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit) return;
    if (!importFromDeliveryQuery || !/^\d+$/.test(importFromDeliveryQuery)) return;
    if (importFromDeliveryLinesLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const full = (await fetcher(`/supply-deliveries/${importFromDeliveryQuery}`)) as {
          ref?: string | null;
          id?: number;
          lines?: Array<{
            id?: number;
            itemId?: number | null;
            itemVcode?: string | null;
            itemDescription?: string | null;
            unitId?: number | null;
            purchaseOrderQty?: number | null;
            deliveryQty?: number | null;
            isDelivered?: boolean;
            itemNotes?: string | null;
            item?: { level1?: string | null } | null;
            unit?: { id?: number; ucode?: string | null } | null;
          }>;
        };
        if (cancelled) return;
        const dlvRef = (full.ref || '').trim() || `#${full.id ?? importFromDeliveryQuery}`;
        const noteLine = `Follow-up requisition: undelivered or qty mismatch from delivery ${dlvRef} (id ${importFromDeliveryQuery}).`;
        setFormData((s) => ({
          ...s,
          notes: s.notes?.trim() ? `${s.notes.trim()}\n\n${noteLine}` : noteLine,
        }));
        const mapped: LineForm[] = (full.lines || [])
          .map((line) => {
            const qtyN = followUpQtyFromDeliveryLine(line);
            if (qtyN == null || qtyN <= 0) return null;
            return {
              key: `dlv-line-${line.id ?? Date.now()}-${Math.random()}`,
              level1Filter: (line.item?.level1 ?? '').trim(),
              itemId: line.itemId != null ? String(line.itemId) : '',
              itemVcode: line.itemVcode || '',
              itemDescr: line.itemDescription || '',
              unitId: line.unitId != null ? String(line.unitId) : '',
              qty: String(qtyN),
              rob: '',
              itemNotes: line.itemNotes
                ? `${line.itemNotes} (imported from delivery)`
                : 'Imported from delivery (shortfall / pending)',
            } as LineForm;
          })
          .filter((x): x is LineForm => x != null);
        if (mapped.length > 0) {
          setLines(mapped);
          importFromDeliveryLinesLoadedRef.current = true;
        }
      } catch {
        // keep default line row
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, importFromDeliveryQuery]);

  const [saveTemplateName, setSaveTemplateName] = React.useState('');
  const [saveTemplateBusy, setSaveTemplateBusy] = React.useState(false);
  const [pickerLineKey, setPickerLineKey] = React.useState<string | null>(null);
  const [pickerLevel1, setPickerLevel1] = React.useState('');
  const [pickerLevel2, setPickerLevel2] = React.useState('');
  const [pickerLevel3, setPickerLevel3] = React.useState('');
  const [pickerSearch, setPickerSearch] = React.useState('');
  const [pickerPage, setPickerPage] = React.useState(1);
  const [pickerSelectedItems, setPickerSelectedItems] = React.useState<any[]>([]);
  const [expandedLevel1, setExpandedLevel1] = React.useState<Set<string>>(new Set());
  const [expandedLevel2, setExpandedLevel2] = React.useState<Set<string>>(new Set());
  const pickerSelectAllRef = React.useRef<HTMLInputElement>(null);
  const { data: itemsResponse } = useSWR<any>('/store-items?page=1&limit=200', fetcher);
  const items = React.useMemo<any[]>(
    () => (Array.isArray(itemsResponse) ? itemsResponse : itemsResponse?.data ?? []),
    [itemsResponse],
  );
  const selectedLevel1Values = React.useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .map((line) => line.level1Filter?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [lines],
  );
  const { data: levelItemsMap = {} } = useSWR<Record<string, any[]>>(
    selectedLevel1Values.length > 0 ? ['requisition-level-items', ...selectedLevel1Values] : null,
    async () => {
      const entries = await Promise.all(
        selectedLevel1Values.map(async (level1) => {
          const params = new URLSearchParams();
          params.set('filters', JSON.stringify({ filters: { level1: [level1] } }));
          const response = await fetcher(`/store-items?${params.toString()}`);
          const levelItems = Array.isArray(response) ? response : response?.data ?? [];
          return [level1, levelItems] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    { revalidateOnFocus: false },
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    for (const levelItems of Object.values(levelItemsMap)) {
      for (const item of levelItems) {
        map.set(String(item.id), item);
      }
    }
    return map;
  }, [items, levelItemsMap]);
  const pickerOpen = pickerLineKey !== null;
  const pickerCategoriesUrl = pickerOpen ? '/store-items/categories' : null;
  const { data: pickerCategories, isLoading: pickerCategoriesLoading } = useSWR<StoreItemCategoryResponse>(pickerCategoriesUrl, fetcher, {
    revalidateOnFocus: false,
  });
  const pickerItemsUrl = React.useMemo(() => {
    if (!pickerOpen) return null;
    const params = new URLSearchParams();
    params.set('page', String(pickerPage));
    params.set('limit', '80');
    const filters: Record<string, string[]> = {};
    if (pickerLevel1.trim()) {
      filters.level1 = [pickerLevel1.trim()];
    }
    if (pickerLevel2.trim()) {
      filters.level2 = [pickerLevel2.trim()];
    }
    if (pickerLevel3.trim()) {
      filters.level3 = [pickerLevel3.trim()];
    }
    if (Object.keys(filters).length > 0) {
      params.set('filters', JSON.stringify({ filters }));
    }
    const term = pickerSearch.trim();
    if (term) {
      params.set('searchParams', JSON.stringify({ search: term }));
    }
    return `/store-items?${params.toString()}`;
  }, [
    pickerLevel1,
    pickerLevel2,
    pickerLevel3,
    pickerOpen,
    pickerPage,
    pickerSearch,
  ]);
  const { data: pickerItemsResponse, isLoading: pickerItemsLoading } = useSWR<any>(pickerItemsUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const templateSelectOptions = React.useMemo(
    () => [
      { value: '', label: 'Line template…' },
      ...lineTemplates.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [lineTemplates],
  );

  const applyLineTemplate = React.useCallback(() => {
    if (!templateApplyId) return;
    const t = lineTemplates.find((x) => String(x.id) === templateApplyId);
    if (!t?.lines?.length) return;
    const mapped: LineForm[] = t.lines.map((l) => ({
      ...newLine(),
      itemId: l.itemId != null ? String(l.itemId) : '',
      itemVcode: l.itemVcode || '',
      itemDescr: l.itemDescr || '',
      unitId: l.unitId != null ? String(l.unitId) : '',
      qty: l.qty !== null && l.qty !== undefined ? String(l.qty) : '',
      rob: l.rob !== null && l.rob !== undefined ? String(l.rob) : '',
      itemNotes: l.itemNotes || '',
      level1Filter: (l.item?.level1 ?? '').trim(),
    }));
    setLines((prev) => [...prev, ...mapped]);
  }, [lineTemplates, templateApplyId]);

  const handleExcelImportChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setImportExcelBusy(true);
      try {
        const data = await importSupplyRequisitionFromExcel(file);
        const importedRef = data.ref?.trim();
        if (importedRef) setFormData((s) => ({ ...s, ref: importedRef }));
        const importedReqDate = data.reqDate?.trim();
        if (importedReqDate) setFormData((s) => ({ ...s, reqDate: importedReqDate }));
        if (data.vesselId != null) setFormData((s) => ({ ...s, vesselId: String(data.vesselId) }));
        if (data.categoryIds?.length) setFormData((s) => ({ ...s, categoryIds: [...data.categoryIds] }));
        if (data.notes?.trim()) {
          setFormData((s) => ({
            ...s,
            notes: s.notes?.trim() ? `${s.notes.trim()}\n\n${data.notes}` : data.notes ?? '',
          }));
        }
        if (data.lines.length > 0) {
          setLines(
            data.lines.map((l) => ({
              key: `xlsx-${Date.now()}-${Math.random()}`,
              level1Filter: l.level1Filter || '',
              itemId: l.itemId != null ? String(l.itemId) : '',
              itemVcode: l.itemVcode || '',
              itemDescr: l.itemDescr || '',
              unitId: l.unitId != null ? String(l.unitId) : '',
              qty: l.qty != null && l.qty !== undefined ? String(l.qty) : '',
              rob: l.rob != null && l.rob !== undefined ? String(l.rob) : '',
              itemNotes: l.itemNotes || '',
            })),
          );
        }
        const w = data.warnings?.length ? ` ${data.warnings.join(' ')}` : '';
        const summary =
          data.lineCount > 0
            ? `Imported ${data.lineCount} line(s) from Excel.`
            : 'No data rows found in the mapped line area (header fields may still have been applied).';
        showSuccess(`${summary}${w}`);
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: unknown } } };
        const raw = ax.response?.data?.message;
        const msg =
          typeof raw === 'string'
            ? raw
            : Array.isArray(raw)
              ? raw.join(' ')
              : err instanceof Error
                ? err.message
                : 'Excel import failed.';
        showError(msg);
      } finally {
        setImportExcelBusy(false);
      }
    },
    [showError, showSuccess],
  );

  const caseOptions = React.useMemo(() => {
    const rows = [
      { value: '', label: 'No case' },
      ...cases.map((c: { id: number; ref?: string | null }) => ({
        value: String(c.id),
        label: c.ref || `Case #${c.id}`,
      })),
    ];
    const sc = supplyCaseForPrefill;
    if (sc?.id != null && !rows.some((o) => o.value === String(sc.id))) {
      rows.push({ value: String(sc.id), label: sc.ref || `Case #${sc.id}` });
    }
    return rows;
  }, [cases, supplyCaseForPrefill]);

  const vesselOptions = React.useMemo(() => {
    const rows = [
      { value: '', label: 'No vessel' },
      ...vessels.map((v) => ({ value: String(v.id), label: v.vesselName || `Vessel #${v.id}` })),
    ];
    const v = supplyCaseForPrefill?.vessel;
    if (v?.id != null && !rows.some((o) => o.value === String(v.id))) {
      rows.push({ value: String(v.id), label: v.vesselName || `Vessel #${v.id}` });
    }
    return rows;
  }, [vessels, supplyCaseForPrefill]);
  const requisitionCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = Number(formData.caseId);
    if (!Number.isFinite(caseIdN) || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.supplyCase ?? {
          id: caseIdN,
        },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, formData.caseId, existing?.supplyCase]);
  const lockVesselField = React.useMemo(() => {
    const caseIdN = Number(formData.caseId);
    return lockCaseContext || (isEdit && Number.isFinite(caseIdN) && caseIdN > 0);
  }, [lockCaseContext, isEdit, formData.caseId]);

  const level1Options = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const L = (it.level1 ?? '').trim();
      if (L) set.add(L);
    }
    return [
      { value: '', label: 'All' },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [items]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const f = line.level1Filter?.trim();
      const filtered = f ? levelItemsMap[f] ?? [] : items;
      const options = [
        { value: '', label: 'Select item' },
        ...filtered.map((item) => ({
          value: String(item.id),
          label: formatRequisitionItemLabel(
            item.vendorCode,
            item.name,
            item.unitId,
            units,
            item.unit?.ucode,
          ),
        })),
      ];
      if (line.itemId && !options.some((o) => o.value === line.itemId)) {
        options.push({
          value: line.itemId,
          label: formatRequisitionItemLabel(line.itemVcode, line.itemDescr, line.unitId, units),
        });
      }
      return options;
    },
    [items, levelItemsMap, units],
  );

  const unitOptions = React.useMemo(
    () => [
      { value: '', label: 'Unit' },
      ...units.map((unit) => ({ value: String(unit.id), label: unit.descr || unit.ucode || `Unit #${unit.id}` })),
    ],
    [units],
  );
  const pickerList = React.useMemo(
    () =>
      Array.isArray(pickerItemsResponse)
        ? { data: pickerItemsResponse, total: pickerItemsResponse.length, page: 1, limit: 80, totalPages: 1 }
        : pickerItemsResponse ?? { data: [], total: 0, page: pickerPage, limit: 80, totalPages: 1 },
    [pickerItemsResponse, pickerPage],
  );
  const pickerItems = React.useMemo<any[]>(() => pickerList.data ?? [], [pickerList.data]);
  const pickerTotalPages = React.useMemo(
    () =>
      typeof pickerList.totalPages === 'number'
        ? Math.max(1, pickerList.totalPages)
        : Math.max(1, Math.ceil((pickerList.total ?? 0) / (pickerList.limit || 80))),
    [pickerList.limit, pickerList.total, pickerList.totalPages],
  );
  const pickerSelectedLine = React.useMemo(
    () => lines.find((line) => line.key === pickerLineKey) ?? null,
    [lines, pickerLineKey],
  );
  const pickerSelectedIds = React.useMemo(
    () => new Set(pickerSelectedItems.map((item) => String(item.id))),
    [pickerSelectedItems],
  );
  const allPagePickerSelected =
    pickerItems.length > 0 && pickerItems.every((item) => pickerSelectedIds.has(String(item.id)));
  const somePagePickerSelected = pickerItems.some((item) => pickerSelectedIds.has(String(item.id)));
  const pickerSelectionUnique = React.useMemo(() => {
    const unique: any[] = [];
    const seen = new Set<string>();
    for (const item of pickerSelectedItems) {
      const id = String(item.id);
      if (!seen.has(id)) {
        seen.add(id);
        unique.push(item);
      }
    }
    return unique;
  }, [pickerSelectedItems]);
  const existingLineItemIdsExcludingTarget = React.useMemo(() => {
    const ids = new Set<string>();
    for (const line of lines) {
      if (line.key === pickerLineKey) continue;
      if (line.itemId) ids.add(line.itemId);
    }
    return ids;
  }, [lines, pickerLineKey]);
  const pickerInsertableItems = React.useMemo(
    () =>
      pickerSelectionUnique.filter(
        (item) => !existingLineItemIdsExcludingTarget.has(String(item.id)),
      ),
    [existingLineItemIdsExcludingTarget, pickerSelectionUnique],
  );
  const pickerSkippedDuplicates = pickerSelectionUnique.length - pickerInsertableItems.length;
  const pickerEstimatedAddedLines =
    pickerInsertableItems.length > 0 ? Math.max(0, pickerInsertableItems.length - 1) : 0;
  const pickerBreadcrumb = React.useMemo(
    () =>
      [pickerLevel1.trim(), pickerLevel2.trim(), pickerLevel3.trim()]
        .filter((value) => value.length > 0)
        .join(' / '),
    [pickerLevel1, pickerLevel2, pickerLevel3],
  );
  const openItemPicker = (line: LineForm) => {
    const initialLevel1 = line.level1Filter?.trim() ?? '';
    setPickerLineKey(line.key);
    setPickerLevel1(initialLevel1);
    setPickerLevel2('');
    setPickerLevel3('');
    setPickerSearch('');
    setPickerPage(1);
    setPickerSelectedItems([]);
    setExpandedLevel1(initialLevel1 ? new Set([initialLevel1]) : new Set());
    setExpandedLevel2(new Set());
  };
  const applySelectedItemsToLines = React.useCallback(() => {
    if (!pickerLineKey || pickerInsertableItems.length === 0) return;
    setLines((prev) => {
      const targetIndex = prev.findIndex((line) => line.key === pickerLineKey);
      if (targetIndex < 0) {
        return prev;
      }
      const next = [...prev];
      const toLinePatch = (item: any): Partial<LineForm> => ({
        itemId: String(item.id),
        itemDescr: item.name || '',
        itemVcode: item.vendorCode || '',
        unitId: item.unitId ? String(item.unitId) : '',
        level1Filter: (item.level1 ?? '').trim(),
      });

      next[targetIndex] = {
        ...next[targetIndex],
        ...toLinePatch(pickerInsertableItems[0]),
      };

      const extraLines = pickerInsertableItems.slice(1).map((item) => ({
        ...newLine(),
        ...toLinePatch(item),
      }));
      if (extraLines.length > 0) {
        next.splice(targetIndex + 1, 0, ...extraLines);
      }
      return next;
    });
  }, [pickerInsertableItems, pickerLineKey]);

  const closeItemPicker = () => {
    setPickerLineKey(null);
    setPickerSearch('');
    setPickerPage(1);
    setPickerSelectedItems([]);
    setExpandedLevel1(new Set());
    setExpandedLevel2(new Set());
  };

  const insertSelectedAndClose = () => {
    applySelectedItemsToLines();
    closeItemPicker();
  };

  const removePickerSelection = (itemId: string) => {
    setPickerSelectedItems((prev) => prev.filter((row) => String(row.id) !== itemId));
  };
  const clearPickerSelection = () => {
    setPickerSelectedItems([]);
  };
  const applyItemToLine = (lineKey: string, item: any) => {
    setLine(lineKey, {
      itemId: String(item.id),
      itemDescr: item.name || '',
      itemVcode: item.vendorCode || '',
      unitId: item.unitId ? String(item.unitId) : '',
      level1Filter: (item.level1 ?? '').trim(),
    });
  };
  const togglePickerSelection = (item: any) => {
    setPickerSelectedItems((prev) => {
      const itemId = String(item.id);
      const exists = prev.some((row) => String(row.id) === itemId);
      if (exists) {
        return prev.filter((row) => String(row.id) !== itemId);
      }
      return [...prev, item];
    });
  };
  const toggleSelectAllPickerPage = () => {
    setPickerSelectedItems((prev) => {
      const pageIds = new Set(pickerItems.map((item) => String(item.id)));
      const next = [...prev];
      if (allPagePickerSelected) {
        return next.filter((item) => !pageIds.has(String(item.id)));
      }
      const existingIds = new Set(next.map((item) => String(item.id)));
      for (const item of pickerItems) {
        const id = String(item.id);
        if (!existingIds.has(id)) {
          next.push(item);
          existingIds.add(id);
        }
      }
      return next;
    });
  };

  /** Backfill Level 1 for rows that still don't have it after catalog pages resolve. */
  React.useEffect(() => {
    if (!itemsById.size) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.itemId || line.level1Filter?.trim()) return line;
        const item = itemsById.get(line.itemId);
        if (!item) return line;
        changed = true;
        return { ...line, level1Filter: (item.level1 ?? '').trim() };
      });
      return changed ? next : prev;
    });
  }, [itemsById, requisitionId]);

  const setLine = (key: string, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const onLevel1FilterChange = (lineKey: string, value: string) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== lineKey) return line;
        const next: LineForm = { ...line, level1Filter: value };
        const f = value.trim();
        const previousLevel1 = (line.level1Filter ?? '').trim();
        if (f && line.itemId && previousLevel1 !== f) {
          next.itemId = '';
          next.itemDescr = '';
          next.itemVcode = '';
          next.unitId = '';
        }
        return next;
      }),
    );
  };

  const onItemChange = (lineKey: string, itemId: string) => {
    if (!itemId) {
      setLine(lineKey, { itemId: '', itemDescr: '', itemVcode: '', unitId: '' });
      return;
    }
    const item = itemsById.get(itemId);
    setLine(lineKey, {
      itemId,
      itemDescr: item?.name || '',
      itemVcode: item?.vendorCode || '',
      unitId: item?.unitId ? String(item.unitId) : '',
      level1Filter: (item?.level1 ?? '').trim(),
    });
  };
  React.useEffect(() => {
    setPickerPage(1);
  }, [pickerLevel1, pickerLevel2, pickerLevel3, pickerSearch]);

  React.useEffect(() => {
    if (!pickerOpen || !pickerSelectedLine) return;
    const lineLevel1 = pickerSelectedLine.level1Filter?.trim() ?? '';
    if (lineLevel1 && lineLevel1 !== pickerLevel1) {
      setPickerLevel1(lineLevel1);
      setPickerLevel2('');
      setPickerLevel3('');
      setExpandedLevel1(new Set([lineLevel1]));
      setExpandedLevel2(new Set());
    }
  }, [pickerLevel1, pickerOpen, pickerSelectedLine]);

  React.useEffect(() => {
    const checkbox = pickerSelectAllRef.current;
    if (!checkbox) return;
    checkbox.indeterminate = somePagePickerSelected && !allPagePickerSelected;
  }, [allPagePickerSelected, somePagePickerSelected]);

  const normalizedLines: SupplyRequisitionLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        qty: asNumber(line.qty),
        rob: asNumber(line.rob),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter((line) => line.itemId || line.itemDescr || line.qty || line.itemNotes);
  }, [lines]);

  const requisitionHasCaseSelected = React.useMemo(() => {
    const raw = formData.caseId?.trim();
    if (!raw || !/^\d+$/.test(raw)) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0;
  }, [formData.caseId]);

  const submitRequisition = async (saveStatus: 'DRAFT' | 'SAVED') => {
    const missing: string[] = [];
    if (!formData.reqDate.trim()) missing.push('Date');
    if (!formData.ref.trim()) missing.push('Reference');
    if (!formData.vesselId.trim()) missing.push('Vessel');
    if (missing.length > 0) {
      showError(`Required: ${missing.join(', ')}`);
      return;
    }
    const caseIdNum = asNumber(formData.caseId);
    if (saveStatus === 'DRAFT' && caseIdNum != null && caseIdNum > 0) {
      showError(
        'Draft requisitions cannot be linked to a supply case. Clear the case or use Save to finalize the requisition.',
      );
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        reqDate: formData.reqDate || undefined,
        caseId: asNumber(formData.caseId),
        vesselId: asNumber(formData.vesselId),
        categoryIds: formData.categoryIds,
        ref: formData.ref || undefined,
        refN: asNumber(formData.refN),
        notes: formData.notes || undefined,
        lines: normalizedLines,
        saveStatus,
      };
      if (
        !isEdit &&
        hasActiveRequisitionTemplate &&
        supplyRequisitionRefPeekSnapshot.shouldOmitRefForServerAllocation(formData.ref)
      ) {
        delete payload.ref;
        delete payload.refN;
      }
      if (isEdit && requisitionId) {
        await updateSupplyRequisition(requisitionId, payload);
      } else {
        await createSupplyRequisition(payload);
      }
      navigateBackOrFallback(navigate, {
        explicitReturnPath: returnPath,
        fallbackPath: moduleConfig.module.route.list,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const listRoute = moduleConfig.module.route.list;
  const goBack = useNavigateBack(listRoute, returnPath);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submitRequisition('SAVED');
      }}
    >
      <Card
        variant="outlined"
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
          'border shadow-sm',
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
          {isEdit && existing?.saveStatus ? (
            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-xs font-medium',
                existing.saveStatus === 'DRAFT'
                  ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                  : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
              )}
            >
              {existing.saveStatus === 'DRAFT' ? 'Draft' : 'Saved'}
            </span>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={isSaving || loadingExisting || requisitionHasCaseSelected}
            loading={isSaving}
            size="default"
            title={
              requisitionHasCaseSelected
                ? 'Remove the supply case to save a draft, or use Save to link this finalized requisition to the case.'
                : undefined
            }
            onClick={() => void submitRequisition('DRAFT')}
          >
            Save draft
          </Button>
          <Button type="submit" disabled={isSaving || loadingExisting} loading={isSaving} size="default">
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Card>

      {requisitionCaseCaption ? (
        <Callout variant="success" title="Linked supply case">{requisitionCaseCaption}</Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Requisition details"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div
              className={cn(
                'grid w-full gap-4',
                '[grid-template-columns:repeat(auto-fit,minmax(min(100%,11rem),1fr))]',
              )}
            >
              <div className="min-w-0">
                <Label htmlFor="req-date" required>
                  Date
                </Label>
                <DateInput
                  id="req-date"
                  value={formData.reqDate}
                  onChange={(e) => setFormData((s) => ({ ...s, reqDate: e.target.value }))}
                  placeholder="Select date"
                  showFormatHint={false}
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="req-ref" required>
                  Reference
                </Label>
                <TextInput
                  id="req-ref"
                  value={formData.ref}
                  onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))}
                  placeholder={allowManualRequisitionRef ? 'Reference' : ''}
                  showClearButton={allowManualRequisitionRef}
                  readOnly={!allowManualRequisitionRef}
                  title={
                    allowManualRequisitionRef
                      ? undefined
                      : 'Reference is generated from the supply reference template (manual edit disabled for requisitions).'
                  }
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="req-vessel" required>
                  Vessel
                </Label>
                <Select
                  id="req-vessel"
                  name="vesselId"
                  value={formData.vesselId}
                  onChange={(e) => setFormData((s) => ({ ...s, vesselId: e.target.value }))}
                  options={vesselOptions}
                  placeholder="No vessel"
                  showClearButton={false}
                  disabled={lockVesselField}
                />
              </div>
              <div className="min-w-0 md:col-span-2">
                <Label htmlFor="req-categories">Categories</Label>
                <MultiSelect
                  id="req-categories"
                  options={categoryMultiOptions}
                  value={formData.categoryIds}
                  onChange={(v) =>
                    setFormData((s) => ({
                      ...s,
                      categoryIds: v.map((x) => Number(x)).filter((n) => !Number.isNaN(n)),
                    }))
                  }
                  placeholder="Select store categories…"
                  maxDisplay={3}
                />
              </div>
              <div className="min-w-0 md:col-span-2">
                <Label htmlFor="req-notes">Notes</Label>
                <Textarea
                  id="req-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
                  rows={2}
                  showClearButton={false}
                  className="min-h-[72px]"
                />
              </div>
            </div>
          </AccordionItem>
          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8',
                  colors.textSecondary,
                  'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25',
                )}
                title="Delete all lines"
                aria-label="Delete all lines"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteAllLinesOpen(true);
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            }
          >
            <div
              className={cn(
                'mb-3 flex flex-col gap-2 rounded-lg border p-2 sm:mb-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1.5 sm:p-1.5 sm:pl-2.5',
                colors.border,
                colors.surface,
              )}
              title="Load or save line lists. Requisition header is not stored in a template."
            >
              <span
                className={cn(
                  'shrink-0 pl-0.5 text-[0.65rem] font-semibold uppercase leading-none tracking-wide',
                  colors.textSecondary,
                )}
              >
                Templates
              </span>
              <div className="min-w-0 flex-1 sm:max-w-sm">
                <Label htmlFor="requisition-line-template" className="sr-only">
                  Line template
                </Label>
                <Select
                  id="requisition-line-template"
                  name="lineTemplate"
                  value={templateApplyId}
                  onChange={(e) => setTemplateApplyId(e.target.value)}
                  options={templateSelectOptions}
                  placeholder="Saved template…"
                  showClearButton={false}
                  className="h-8 w-full text-sm"
                />
              </div>
              <div
                className="flex flex-wrap items-center gap-1.5 sm:shrink-0"
                role="group"
                aria-label="Template actions"
              >
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={!templateApplyId}
                  onClick={applyLineTemplate}
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" />
                  Apply
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={() => {
                    setSaveTemplateName('');
                    setSaveTemplateOpen(true);
                  }}
                >
                  <FolderPlusIcon className="h-3.5 w-3.5" />
                  New…
                </Button>
              </div>
              <div
                className={cn('flex w-full sm:ml-auto sm:w-auto sm:shrink-0 sm:border-l sm:pl-2', colors.border)}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-full gap-1 sm:w-auto"
                  onClick={() => setLines((prev) => [...prev, newLine()])}
                  title="Add a blank line"
                  aria-label="Add line"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add line
                </Button>
              </div>
              <>
                <input
                  ref={excelImportInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={handleExcelImportChange}
                />
                <div
                  className={cn('flex w-full sm:w-auto sm:shrink-0 sm:border-l sm:pl-2', colors.border)}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 w-full gap-1 sm:w-auto"
                    disabled={importExcelBusy}
                    loading={importExcelBusy}
                    onClick={() => excelImportInputRef.current?.click()}
                    title="Merge data from an .xlsx into this form using the active REQUISITION template (Administration → Excel export templates). Replaces line rows when the file contains lines."
                  >
                    <UploadCloudIcon className="h-3.5 w-3.5" />
                    Import Excel
                  </Button>
                </div>
              </>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="px-2 py-2 text-left text-xs font-medium"> </th>
                    <th className="w-[10rem] min-w-[10rem] max-w-[10rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] px-2 py-2 text-left text-xs font-medium">Unit</th>
                    <th className="w-24 min-w-[6rem] max-w-[6rem] px-2 py-2 text-left text-xs font-medium">Qty</th>
                    <th className="w-24 min-w-[6rem] max-w-[6rem] px-2 py-2 text-left text-xs font-medium">ROB</th>
                    <th className="w-[12rem] min-w-[12rem] max-w-[12rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.key} className="align-top">
                      <td className="p-2 w-14 align-middle">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          title="Find item"
                          aria-label="Find item"
                          onClick={() => openItemPicker(line)}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </Button>
                      </td>
                      <td className="w-[10rem] min-w-[10rem] max-w-[10rem] p-2">
                        <Select
                          value={line.level1Filter}
                          onChange={(e) => onLevel1FilterChange(line.key, e.target.value)}
                          options={level1Options}
                          placeholder="Level 1"
                          showClearButton={false}
                          className="w-full text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={line.itemId}
                          onChange={(e) => onItemChange(line.key, e.target.value)}
                          options={getItemOptionsForLine(line)}
                          placeholder="Select item"
                          showClearButton={false}
                          className="w-full text-sm"
                        />
                      </td>
                      <td className="w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem] p-2">
                        <Select
                          value={line.unitId}
                          onChange={(e) => setLine(line.key, { unitId: e.target.value })}
                          options={unitOptions}
                          placeholder="Unit"
                          showClearButton={false}
                        />
                      </td>
                      <td className="p-2 w-24 align-middle">
                        <NumberInput
                          size="sm"
                          value={line.qty}
                          onChange={(e) =>
                            setLine(line.key, {
                              qty: e.target.value === null || e.target.value === undefined ? '' : String(e.target.value),
                            })
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2 w-24 align-middle">
                        <NumberInput
                          size="sm"
                          value={line.rob}
                          onChange={(e) =>
                            setLine(line.key, {
                              rob: e.target.value === null || e.target.value === undefined ? '' : String(e.target.value),
                            })
                          }
                          showClearButton={false}
                          placeholder="ROB"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="w-[12rem] min-w-[12rem] max-w-[12rem] p-2">
                        <TextInput
                          size="sm"
                          value={line.itemNotes}
                          onChange={(e) => setLine(line.key, { itemNotes: e.target.value })}
                          showClearButton={false}
                          placeholder="Notes"
                        />
                      </td>
                      <td className="p-2 text-right align-middle">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          title={lines.length > 1 ? 'Remove line' : 'Clear line'}
                          aria-label={lines.length > 1 ? 'Remove line' : 'Clear line'}
                          onClick={() =>
                            setLines((prev) => {
                              if (prev.length > 1) {
                                return prev.filter((r) => r.key !== line.key);
                              }
                              return [newLine()];
                            })
                          }
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <Modal isOpen={pickerOpen} onClose={closeItemPicker} size="xl" zIndex={110}>
        <ModalHeader>
          <div className="flex w-full items-center justify-between gap-3">
            <ModalTitle>Find items</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Close"
              aria-label="Close"
              onClick={() => closeItemPicker()}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </ModalHeader>
        <ModalContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className={cn('md:col-span-4 rounded-lg border p-3', colors.border)}>
              <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Categories</p>
              <p className={cn('mb-2 text-xs', colors.textMuted)}>
                Path: {pickerBreadcrumb || 'All'}
              </p>
              <div className="space-y-3 max-h-[55vh] overflow-auto">
                {pickerCategoriesLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={`tree-skeleton-${idx}`}
                        className={cn('h-7 animate-pulse rounded-md', colors.surfaceHover)}
                      />
                    ))}
                  </div>
                ) : (
                  (pickerCategories?.level1 ?? []).map((level1) => {
                  const level1Active = pickerLevel1 === level1;
                  const level1Expanded = expandedLevel1.has(level1);
                  const level2ForLevel1 = pickerCategories?.level2ByLevel1?.[level1] ?? [];
                  return (
                    <div key={`l1-tree-${level1}`} className="space-y-1">
                      <div className={cn('flex items-center gap-1 rounded-md px-1 py-0.5', level1Active ? `${colors.primary} ${colors.primaryText}` : `${colors.surfaceHover}`)}>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedLevel1((prev) => {
                              const next = new Set(prev);
                              if (next.has(level1)) next.delete(level1);
                              else next.add(level1);
                              return next;
                            })
                          }
                          className={cn('rounded p-1', level1Active ? colors.primaryText : colors.textMuted)}
                          title={level1Expanded ? 'Collapse' : 'Expand'}
                          aria-label={level1Expanded ? 'Collapse' : 'Expand'}
                        >
                          <svg className={cn('h-3.5 w-3.5 transition-transform', level1Expanded ? 'rotate-90' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (level1Active) {
                              setPickerLevel1('');
                              setPickerLevel2('');
                              setPickerLevel3('');
                              return;
                            }
                            setPickerLevel1(level1);
                            setPickerLevel2('');
                            setPickerLevel3('');
                            setExpandedLevel1((prev) => {
                              const next = new Set(prev);
                              next.add(level1);
                              return next;
                            });
                          }}
                          className="flex-1 rounded px-1 py-1 text-left text-sm"
                        >
                          {level1}
                        </button>
                      </div>

                      {level1Expanded ? (
                        <div className="space-y-1 pl-4">
                          {level2ForLevel1.length === 0 ? (
                            <p className={cn('px-2 py-1 text-xs', colors.textMuted)}>No Level 2 values</p>
                          ) : (
                            level2ForLevel1.map((level2) => {
                              const level2Active = pickerLevel2 === level2;
                              const level2PathKey = `${level1}::${level2}`;
                              const level2Expanded = expandedLevel2.has(level2PathKey);
                              const level3ForPath =
                                pickerCategories?.level3ByLevel1Level2?.[JSON.stringify([level1, level2])] ?? [];
                              return (
                                <div key={`l2-tree-${level1}-${level2}`} className="space-y-1">
                                  <div className={cn('flex items-center gap-1 rounded-md px-1 py-0.5', level2Active ? `${colors.primary} ${colors.primaryText}` : `${colors.surfaceHover}`)}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedLevel2((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(level2PathKey)) next.delete(level2PathKey);
                                          else next.add(level2PathKey);
                                          return next;
                                        })
                                      }
                                      className={cn('rounded p-1', level2Active ? colors.primaryText : colors.textMuted)}
                                      title={level2Expanded ? 'Collapse' : 'Expand'}
                                      aria-label={level2Expanded ? 'Collapse' : 'Expand'}
                                    >
                                      <svg className={cn('h-3.5 w-3.5 transition-transform', level2Expanded ? 'rotate-90' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (level2Active) {
                                          setPickerLevel2('');
                                          setPickerLevel3('');
                                          return;
                                        }
                                        setPickerLevel2(level2);
                                        setPickerLevel3('');
                                        setExpandedLevel2((prev) => {
                                          const next = new Set(prev);
                                          next.add(level2PathKey);
                                          return next;
                                        });
                                      }}
                                      className="flex-1 rounded px-1 py-1 text-left text-sm"
                                    >
                                      {level2}
                                    </button>
                                  </div>

                                  {level2Expanded ? (
                                    <div className="space-y-1 pl-4">
                                      {level3ForPath.length === 0 ? (
                                        <p className={cn('px-2 py-1 text-xs', colors.textMuted)}>No Level 3 values</p>
                                      ) : (
                                        level3ForPath.map((level3) => {
                                          const level3Active = pickerLevel3 === level3;
                                          return (
                                            <button
                                              key={`l3-tree-${level1}-${level2}-${level3}`}
                                              type="button"
                                              onClick={() => setPickerLevel3(level3)}
                                              className={cn(
                                                'w-full rounded-md px-2 py-1 text-left text-sm transition',
                                                level3Active
                                                  ? `${colors.primary} ${colors.primaryText}`
                                                  : `${colors.text} ${colors.surfaceHover}`,
                                              )}
                                            >
                                              {level3}
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                  })
                )}
              </div>
            </div>

            <div className="md:col-span-8">
              <div className="mb-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder="Search item by code, name, notes or levels..."
                    className={cn(
                      'w-full rounded-lg border px-3 py-2.5 pr-10 text-base',
                      colors.border,
                      colors.surface,
                      colors.text,
                    )}
                  />
                  {pickerSearch ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      title="Clear search"
                      aria-label="Clear search"
                      className={cn(
                        'absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7',
                        colors.surface,
                        colors.border,
                      )}
                      onClick={() => setPickerSearch('')}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9l6 6m0-6l-6 6" />
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    </Button>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  title="Reset filters"
                  aria-label="Reset filters"
                  onClick={() => {
                    setPickerLevel1('');
                    setPickerLevel2('');
                    setPickerLevel3('');
                    setPickerSearch('');
                  }}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9M20 20v-5h-.581m0 0A8.003 8.003 0 014.642 13" />
                  </svg>
                </Button>
              </div>

              {pickerSelectedItems.length > 0 ? (
                <div className={cn('mb-3 rounded-lg border p-2', colors.border)}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className={cn('text-xs font-medium', colors.textSecondary)}>
                      Selected items ({pickerSelectedItems.length})
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearPickerSelection}
                    >
                      Clear selected
                    </Button>
                  </div>
                  <div className="max-h-24 space-y-1 overflow-auto">
                    {pickerSelectedItems.map((item) => (
                      <div key={`sel-${item.id}`} className={cn('flex items-center justify-between rounded px-2 py-1 text-xs', colors.surfaceHover)}>
                        <span className={cn('truncate', colors.text)}>
                          {item.vendorCode || '-'} - {item.name || '-'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Remove selected item"
                          aria-label="Remove selected item"
                          onClick={() => removePickerSelection(String(item.id))}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className={cn('rounded-lg border', colors.border)}>
                <div className={cn('max-h-[46vh] overflow-auto')}>
                  <table className="min-w-full text-sm">
                    <thead className={cn('sticky top-0', colors.surface)}>
                      <tr className={cn('border-b', colors.border)}>
                        <th className="px-2 py-2 text-left text-xs font-medium">
                          <input
                            ref={pickerSelectAllRef}
                            type="checkbox"
                            checked={allPagePickerSelected && pickerItems.length > 0}
                            onChange={toggleSelectAllPickerPage}
                            className="rounded"
                            title="Select all on page"
                            aria-label="Select all on page"
                          />
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-medium">Code</th>
                        <th className="px-2 py-2 text-left text-xs font-medium">Name</th>
                        <th className="px-2 py-2 text-left text-xs font-medium">Levels</th>
                        <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickerItemsLoading ? (
                        Array.from({ length: 6 }).map((_, idx) => (
                          <tr key={`item-skeleton-${idx}`} className={cn('border-b', colors.border)}>
                            <td className="px-2 py-2">
                              <div className={cn('h-4 w-4 animate-pulse rounded', colors.surfaceHover)} />
                            </td>
                            <td className="px-2 py-2">
                              <div className={cn('h-4 w-20 animate-pulse rounded', colors.surfaceHover)} />
                            </td>
                            <td className="px-2 py-2">
                              <div className={cn('h-4 w-40 animate-pulse rounded', colors.surfaceHover)} />
                            </td>
                            <td className="px-2 py-2">
                              <div className={cn('h-4 w-48 animate-pulse rounded', colors.surfaceHover)} />
                            </td>
                            <td className="px-2 py-2 text-right">
                              <div className={cn('ml-auto h-8 w-8 animate-pulse rounded', colors.surfaceHover)} />
                            </td>
                          </tr>
                        ))
                      ) : pickerItems.length === 0 ? (
                        <tr>
                          <td className={cn('px-2 py-3 text-sm', colors.textMuted)} colSpan={5}>
                            No items found.
                          </td>
                        </tr>
                      ) : (
                        pickerItems.map((item) => (
                          <tr key={String(item.id)} className={cn('border-b', colors.border)}>
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                checked={pickerSelectedItems.some((row) => String(row.id) === String(item.id))}
                                onChange={() => togglePickerSelection(item)}
                                className="rounded"
                                aria-label={`Select item ${item.name || item.vendorCode || item.id}`}
                              />
                            </td>
                            <td className="px-2 py-2">{item.vendorCode || '-'}</td>
                            <td className="px-2 py-2">{item.name || '-'}</td>
                            <td className={cn('px-2 py-2 text-xs', colors.textMuted)}>
                              {[item.level1, item.level2, item.level3].filter(Boolean).join(' / ') || '-'}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                title="Use item"
                                aria-label="Use item"
                                onClick={() => {
                                  if (!pickerLineKey) return;
                                  applyItemToLine(pickerLineKey, item);
                                  closeItemPicker();
                                }}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className={cn('flex items-center justify-between border-t px-3 py-2 text-xs', colors.border, colors.textMuted)}>
                  <span>
                    {pickerList.total ?? 0} result{(pickerList.total ?? 0) === 1 ? '' : 's'}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={pickerPage <= 1}
                      onClick={() => setPickerPage((prev) => Math.max(1, prev - 1))}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {pickerPage} / {pickerTotalPages}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={pickerPage >= pickerTotalPages}
                      onClick={() => setPickerPage((prev) => Math.min(pickerTotalPages, prev + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="sticky bottom-0">
          <div className="flex w-full items-center justify-between gap-3">
            <div className={cn('text-xs', colors.textMuted)}>
              <span className={colors.text}>
                {pickerSelectedItems.length} selected
              </span>
              {' • '}
              <span>estimated +{pickerEstimatedAddedLines} lines</span>
              {pickerSkippedDuplicates > 0 ? (
                <span className={cn('ml-2', colors.warning || colors.textMuted)}>
                  ({pickerSkippedDuplicates} duplicate{pickerSkippedDuplicates === 1 ? '' : 's'} skipped)
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={closeItemPicker}>
                Close
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={pickerInsertableItems.length === 0}
                onClick={insertSelectedAndClose}
              >
                Insert selected ({pickerInsertableItems.length})
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteAllLinesOpen} onClose={() => setDeleteAllLinesOpen(false)} size="md" zIndex={120}>
        <ModalHeader>
          <ModalTitle>Delete all lines?</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm', colors.textSecondary)}>
            All {lines.length} line{lines.length === 1 ? '' : 's'} will be removed and replaced with a single empty row. You
            can still use Save draft or the main form actions as usual.
          </p>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteAllLinesOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => {
              setLines([newLine()]);
              setDeleteAllLinesOpen(false);
            }}
          >
            Delete all
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} size="md" zIndex={120}>
        <ModalHeader>
          <ModalTitle>Save as new line template</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tpl-name">Name</Label>
              <TextInput
                id="tpl-name"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                placeholder="e.g. Monthly provisions"
                showClearButton
              />
            </div>
            <p className={cn('text-xs', colors.textMuted)}>
              Stores the current lines only (requisition header is not included).
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setSaveTemplateOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={saveTemplateBusy}
            disabled={!saveTemplateName.trim() || normalizedLines.length === 0}
            onClick={async () => {
              const name = saveTemplateName.trim();
              if (!name || normalizedLines.length === 0) return;
              setSaveTemplateBusy(true);
              try {
                await createSupplyRequisitionTemplate({
                  name,
                  lines: normalizedLines as Record<string, unknown>[],
                });
                setSaveTemplateOpen(false);
                setSaveTemplateName('');
              } finally {
                setSaveTemplateBusy(false);
              }
            }}
          >
            Save template
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

export default Form;
